import { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { LocationData } from '../types';
import { useFetchNetworkBoundries } from '../../features/geospatialProjects/hooks/useFetchNetworkBoundries';
import { NetworkBoundariesLayerMapbox } from './NetworkBoundariesLayerMapbox';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useFetchLocationDetails } from '../../mutations';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const UK_CENTER: [number, number] = [-1.895662, 52.489706];
const INITIAL_ZOOM = 6;
const UK_BOUNDS: [number, number, number, number] = [
    -10.8544921875, 49.82380908513249, 1.778564453125, 59.478568831926395,
];

interface MapProps {
    network: number | null;
    initialLocation: {
        address: string;
        what3_words: string;
        coordinates: [number, number] | null;
    };
    onLocationChange: (locationData: LocationData) => void;
    disabled: boolean;
}

export const Map: React.FC<MapProps> = ({ network, initialLocation, onLocationChange, disabled }) => {

    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);
    const geocoderRef = useRef<MapboxGeocoder | null>(null);
    const onLocationChangeRef = useRef(onLocationChange);
    const isInitializedRef = useRef(false);
    const networkBoundariesRef = useRef<any[]>([]);
    const selectedLocationRef = useRef<LocationData | null>(null);
    const reverseGeocodeRef = useRef<((coords: [number, number]) => Promise<any>) | undefined>(undefined);
    const updateMarkerRef = useRef<((coords: [number, number], skipValidation?: boolean) => boolean | undefined) | undefined>(undefined);
    const initialLocationRef = useRef<string | null>(null);
    const disabledRef = useRef<boolean>(disabled);

    const [map, setMap] = useState<mapboxgl.Map | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
    const [locationError, setLocationError] = useState<string | null>(null);

    useEffect(() => {
        // If initial location is provided, set it as selected location
        if (initialLocation && initialLocation.coordinates) {
            setSelectedLocation({
                coordinates: initialLocation.coordinates,
                address: initialLocation.address,
                placeName: '', // Place name is not provided in initialLocation, can be fetched if needed
                postcode: '', // Postcode is not provided in initialLocation, can be fetched if needed
                what3_words: initialLocation.what3_words,
                country: 'United Kingdom',
            });
        }
    }, [initialLocation]);

    const theme = useTheme();
    const { networkBoundaries, error: boundariesError, isLoading: isBoundariesLoading } = useFetchNetworkBoundries();
    const fetchMapService = useFetchLocationDetails();

    // Filter boundaries based on network prop
    const filteredBoundaries = networkBoundaries?.filter(boundary =>
        network === null || boundary.properties.id === network
    ) || [];

    // Keep refs updated
    useEffect(() => {
        onLocationChangeRef.current = onLocationChange;
    }, [onLocationChange]);

    useEffect(() => {
        disabledRef.current = disabled;

        if (!mapRef.current) return;

        markerRef.current?.setDraggable(!disabled);

        if (disabled) {
            mapRef.current.dragPan.disable();
            mapRef.current.scrollZoom.disable();
            mapRef.current.boxZoom.disable();
            mapRef.current.dragRotate.disable();
            mapRef.current.keyboard.disable();
            mapRef.current.doubleClickZoom.disable();
            mapRef.current.touchZoomRotate.disable();
        } else {
            mapRef.current.dragPan.enable();
            mapRef.current.scrollZoom.enable();
            mapRef.current.boxZoom.enable();
            mapRef.current.dragRotate.enable();
            mapRef.current.keyboard.enable();
            mapRef.current.doubleClickZoom.enable();
            mapRef.current.touchZoomRotate.enable();
        }
    }, [disabled]);

    useEffect(() => {
        networkBoundariesRef.current = filteredBoundaries;
    }, [filteredBoundaries]);

    useEffect(() => {
        selectedLocationRef.current = selectedLocation;
    }, [selectedLocation]);

    // Auto-dismiss location error after 5 seconds
    useEffect(() => {
        if (locationError) {
            const timer = setTimeout(() => {
                setLocationError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [locationError]);

    // Point-in-polygon check using ray-casting algorithm
    const isPointInPolygon = useCallback((point: [number, number], polygon: number[][]) => {
        const [x, y] = point;
        let inside = false;

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];

            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    }, []);

    // Check if point is within any network boundary
    const isPointInNetworkBoundaries = useCallback((coordinates: [number, number]) => {
        const boundaries = networkBoundariesRef.current;

        if (!boundaries || boundaries.length === 0) {
            // If boundaries haven't loaded yet, disallow selection
            return false;
        }

        for (const feature of boundaries) {
            const { geometry } = feature;

            if (geometry.type === 'Polygon') {
                const rings = geometry.coordinates as number[][][];
                // Check the outer ring (first ring)
                if (isPointInPolygon(coordinates, rings[0])) {
                    // Check if point is in any hole (inner rings)
                    let inHole = false;
                    for (let i = 1; i < rings.length; i++) {
                        if (isPointInPolygon(coordinates, rings[i])) {
                            inHole = true;
                            break;
                        }
                    }
                    if (!inHole) {
                        return true;
                    }
                }
            } else if (geometry.type === 'MultiPolygon') {
                const polygons = geometry.coordinates as number[][][][];
                for (const polygon of polygons) {
                    // Check the outer ring of each polygon
                    if (isPointInPolygon(coordinates, polygon[0])) {
                        // Check if point is in any hole
                        let inHole = false;
                        for (let i = 1; i < polygon.length; i++) {
                            if (isPointInPolygon(coordinates, polygon[i])) {
                                inHole = true;
                                break;
                            }
                        }
                        if (!inHole) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }, [isPointInPolygon]);

    // Helper function for reverse geocoding (stable reference)
    const reverseGeocode = useCallback(async (coordinates: [number, number]) => {
        try {
            const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${coordinates[0]},${coordinates[1]}.json?access_token=${MAPBOX_ACCESS_TOKEN}`);
            const what3_words = await fetchMapService.mutateAsync({
                lat: coordinates[1],
                lng: coordinates[0]
            });
            const data = await response.json();
            const feature = data.features?.[0];

            // Extract postcode from context array
            const postcodeContext = feature?.context?.find((ctx: any) => ctx.id.startsWith('postcode'));
            const postcode = postcodeContext?.text || '';

            return {
                address: feature?.place_name || 'Unknown location',
                placeName: feature?.text || 'Unknown place',
                postcode: postcode,
                what3_words: what3_words?.data?.word || what3_words?.data?.words || '',
                country: 'United Kingdom'
            };
        } catch (error) {
            console.error('Reverse geocoding failed:', error);
            return {
                address: 'Unknown location',
                placeName: 'Unknown place',
                postcode: '',
                what3_words: '',
                country: 'United Kingdom'
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Store in ref
    useEffect(() => {
        reverseGeocodeRef.current = reverseGeocode;
    }, [reverseGeocode]);

    // Update marker
    const updateMarker = useCallback((coordinates: [number, number], skipValidation = false) => {
        if (!mapRef.current) return;

        // Validate location is within network boundaries
        if (!skipValidation) {
            const isValid = isPointInNetworkBoundaries(coordinates);

            if (!isValid) {
                setLocationError('Selected location is outside the network boundaries. Please choose a location within the highlighted areas.');
                return false;
            }
        }

        // Remove existing marker
        markerRef.current?.remove();

        // Create new marker
        markerRef.current = new mapboxgl.Marker({
            color: theme.palette.primary.main,
            draggable: !disabledRef.current,
        })
            .setLngLat(coordinates)
            .addTo(mapRef.current);

        // Handle marker drag
        markerRef.current.on('dragend', async () => {
            if (disabledRef.current) {
                const prevLocation = selectedLocationRef.current;
                if (prevLocation?.coordinates) {
                    markerRef.current?.setLngLat(prevLocation.coordinates);
                }
                return;
            }
            const lngLat = markerRef.current!.getLngLat();
            const coords: [number, number] = [lngLat.lng, lngLat.lat];

            // Validate dragged location
            if (!isPointInNetworkBoundaries(coords)) {
                setLocationError('Selected location is outside the network boundaries. Please choose a location within the highlighted areas.');
                // Reset marker to previous valid position if exists
                const prevLocation = selectedLocationRef.current;
                if (prevLocation?.coordinates) {
                    markerRef.current?.setLngLat(prevLocation.coordinates);
                } else {
                    markerRef.current?.remove();
                    markerRef.current = null;
                }
                return;
            }

            const locationDetails = await reverseGeocode(coords);

            setSelectedLocation({
                coordinates: coords,
                address: locationDetails.address,
                placeName: locationDetails.placeName,
                postcode: locationDetails.postcode,
                what3_words: locationDetails.what3_words,
                country: locationDetails.country,
            });

            onLocationChangeRef.current?.({
                coordinates: coords,
                address: locationDetails.address,
                placeName: locationDetails.placeName,
                postcode: locationDetails.postcode,
                what3_words: locationDetails.what3_words,
                country: locationDetails.country,
            });
        });

        return true;
    }, [theme.palette.primary.main, isPointInNetworkBoundaries, reverseGeocode]);

    // Store in ref
    useEffect(() => {
        updateMarkerRef.current = updateMarker;
    }, [updateMarker]);

    // Initialize map - only once
    useEffect(() => {
        if (!mapContainer.current || isInitializedRef.current || !MAPBOX_ACCESS_TOKEN) {
            if (!MAPBOX_ACCESS_TOKEN) {
                setError('MapBox access token is not configured');
                setIsLoading(false);
            }
            return;
        }

        isInitializedRef.current = true;
        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

        try {
            const mapStyle = theme.palette.mode === 'dark'
                ? 'mapbox://styles/mapbox/dark-v11'
                : 'mapbox://styles/mapbox/standard';

            const mapInstance = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyle,
                center: initialLocation.coordinates || UK_CENTER,
                zoom: initialLocation.coordinates ? 15 : INITIAL_ZOOM,
                trackResize: true,
                minZoom: 6,
            });

            // Add navigation controls
            mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');

            // Add geocoder
            const geocoder = new MapboxGeocoder({
                accessToken: MAPBOX_ACCESS_TOKEN,
                mapboxgl: mapboxgl as any,
                marker: false,
                placeholder: 'Search for places',
                countries: 'gb',
                bbox: UK_BOUNDS,
            });

            mapInstance.addControl(geocoder as unknown as mapboxgl.IControl, 'top-left');
            geocoderRef.current = geocoder;

            // Handle geocoder result
            geocoder.on('result', async (e: any) => {
                if (disabledRef.current) {
                    geocoder.clear();
                    return;
                }
                const coordinates: [number, number] = e.result.center;

                // Validate location is within network boundaries first
                const boundaries = networkBoundariesRef.current;

                if (!boundaries || boundaries.length === 0) {
                    setLocationError('Network boundaries are not loaded yet. Please wait and try again.');
                    geocoder.clear();
                    return;
                }

                let isValid = false;
                for (const feature of boundaries) {
                    const { geometry } = feature;
                    if (geometry.type === 'Polygon') {
                        const rings = geometry.coordinates as number[][][];
                        if (isPointInPolygon(coordinates, rings[0])) {
                            let inHole = false;
                            for (let i = 1; i < rings.length; i++) {
                                if (isPointInPolygon(coordinates, rings[i])) {
                                    inHole = true;
                                    break;
                                }
                            }
                            if (!inHole) {
                                isValid = true;
                                break;
                            }
                        }
                    } else if (geometry.type === 'MultiPolygon') {
                        const polygons = geometry.coordinates as number[][][][];
                        for (const polygon of polygons) {
                            if (isPointInPolygon(coordinates, polygon[0])) {
                                let inHole = false;
                                for (let i = 1; i < polygon.length; i++) {
                                    if (isPointInPolygon(coordinates, polygon[i])) {
                                        inHole = true;
                                        break;
                                    }
                                }
                                if (!inHole) {
                                    isValid = true;
                                    break;
                                }
                            }
                        }
                        if (isValid) break;
                    }
                }

                if (!isValid) {
                    setLocationError('Selected location is outside the network boundaries. Please choose a location within the highlighted areas.');
                    geocoder.clear();
                    return;
                }

                // Validation passed - now fly to location and set marker
                const address = e.result.place_name;
                const locationDetails = await reverseGeocodeRef.current?.(coordinates);

                if (locationDetails) {
                    setSelectedLocation({
                        coordinates,
                        address,
                        placeName: locationDetails.placeName,
                        postcode: locationDetails.postcode,
                        what3_words: locationDetails.what3_words,
                        country: locationDetails.country,
                    });

                    onLocationChangeRef.current?.({
                        coordinates,
                        address,
                        placeName: locationDetails.placeName,
                        postcode: locationDetails.postcode,
                        what3_words: locationDetails.what3_words,
                        country: locationDetails.country
                    });

                    // Fly to the validated location
                    updateMarkerRef.current?.(coordinates, true);
                }
            });

            mapInstance.on('load', () => {
                setIsLoading(false);
                setMap(mapInstance);

                // Add click handler for location selection
                mapInstance.on('click', async (e: mapboxgl.MapMouseEvent) => {
                    if (disabledRef.current) return;
                    const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];

                    // Validate location is within network boundaries using ref
                    const isValid = isPointInNetworkBoundaries(coordinates);

                    if (!isValid) {
                        setLocationError('Selected location is outside the network boundaries. Please choose a location within the highlighted areas.');
                        return;
                    }

                    const locationDetails = await reverseGeocodeRef.current?.(coordinates);

                    if (locationDetails) {
                        setSelectedLocation({
                            coordinates,
                            address: locationDetails.address,
                            placeName: locationDetails.placeName,
                            postcode: locationDetails.postcode,
                            what3_words: locationDetails.what3_words,
                            country: locationDetails.country,
                        });

                        onLocationChangeRef.current?.({
                            coordinates: coordinates,
                            address: locationDetails.address,
                            placeName: locationDetails.placeName,
                            postcode: locationDetails.postcode,
                            what3_words: locationDetails.what3_words,
                            country: locationDetails.country,
                        });
                    }

                    updateMarkerRef.current?.(coordinates, true);
                });

                // Add initial marker if location is provided
                if (initialLocation && initialLocation.coordinates) {
                    updateMarker(initialLocation.coordinates, true); // Skip validation for initial location
                }
            });

            mapInstance.on('error', (e: any) => {
                console.error('MapBox error:', e);
                setError('Failed to load map');
                setIsLoading(false);
            });

            mapRef.current = mapInstance;

            return () => {
                isInitializedRef.current = false;
                markerRef.current?.remove();
                geocoderRef.current?.clear();
                mapInstance.remove();
                mapRef.current = null;
                setMap(null);
            };
        } catch (err) {
            console.error('MapBox initialization error:', err);
            setError('Failed to initialize map');
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme.palette.mode]);

    // Handle initialLocation changes separately to avoid map reinitialization
    useEffect(() => {
        if (map && initialLocation?.coordinates) {
            const coordsKey = JSON.stringify(initialLocation.coordinates);
            // Only update if coordinates actually changed
            if (coordsKey !== initialLocationRef.current) {
                initialLocationRef.current = coordsKey;
                updateMarker(initialLocation.coordinates, true);
            }
        }
    }, [initialLocation?.coordinates?.[0], initialLocation?.coordinates?.[1], map, updateMarker]);

    // Handle theme changes
    useEffect(() => {
        if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

        const mapStyle = theme.palette.mode === 'dark'
            ? 'mapbox://styles/mapbox/dark-v11'
            : 'mapbox://styles/mapbox/standard';

        mapRef.current.setStyle(mapStyle);
    }, [theme.palette.mode]);

    if (error || boundariesError) {
        return (
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                }}
            >
                <Typography color="error" variant="h6" gutterBottom>
                    Map Error
                </Typography>
                <Typography color="text.secondary" variant="body2">
                    {error || (boundariesError instanceof Error ? boundariesError.message : String(boundariesError))}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {(isLoading || isBoundariesLoading) && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 1000,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <Box
                ref={mapContainer}
                sx={{
                    width: '100%',
                    height: '100%',
                    '& .mapboxgl-ctrl-logo': {
                        display: 'none',
                    },
                }}
            />
            {map && <NetworkBoundariesLayerMapbox map={map} features={filteredBoundaries} />}
            {selectedLocation && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        right: 20,
                        maxWidth: 500,
                        zIndex: 1000,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 3,
                        p: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Address
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {selectedLocation.address || "N/A"}
                            </Typography>
                        </Box>
                        {selectedLocation.postcode && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Postcode
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                    {selectedLocation.postcode}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                What3Words
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {selectedLocation.what3_words || "N/A"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
            {locationError && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        right: 20,
                        maxWidth: 500,
                        zIndex: 1000,
                        bgcolor: 'error.light',
                        borderRadius: 2,
                        boxShadow: 3,
                        p: 2,
                        borderColor: 'error.main',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" color="error.contrastText" sx={{ flex: 1 }}>
                            {locationError}
                        </Typography>
                        <Typography
                            variant="body2"
                            component="button"
                            onClick={() => setLocationError(null)}
                            sx={{
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none',
                                color: 'error.contrastText',
                                fontWeight: 600,
                                padding: 0,
                                '&:hover': {
                                    opacity: 0.8,
                                }
                            }}
                        >
                            ✕
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};